import type { t } from '@snowit/system/useLanguage/translator';
import type PERMISSIONS from '@snowit/utilities/permissions';
import type { NavGroup, NavRoute, NavRouteType } from './types';

import { match } from 'path-to-regexp';

type Translate = typeof t;

export const flattenRoute = (
  r: NavRoute,
  parent?: NavRoute,
  skipChildren?: boolean,
): NavRoute[] => {
  const { children: _, ...route } = r;
  const path = [parent?.path, route.path].filter(Boolean).join('/');
  const permissions: PERMISSIONS[] = [
    ...(route?.disowned ? [] : (parent?.permissions ?? [])),
    ...(route.permissions ?? []),
  ];

  const type: NavRouteType = typeof route?.type === 'number' ? route?.type : (parent?.type ?? 0);
  const newRoute = { ...route, type, path, permissions };
  if (skipChildren) return [newRoute];
  const children = (r.children ?? []).reduce(
    (memo, child) => [...memo, ...flattenRoute(child, newRoute)],
    [] as NavRoute[],
  );
  return [newRoute, ...children];
};

export const flattenGroups = (g: NavGroup[], main?: boolean): NavRoute[] => {
  return g.reduce((memo, group) => {
    return [
      ...memo,
      ...group.routes.reduce(
        (memo2, route) => [...memo2, ...flattenRoute(route, undefined, main)],
        [] as NavRoute[],
      ),
    ];
  }, [] as NavRoute[]);
};

export const findByPath = (path: string, g: NavGroup[]): NavRoute | undefined => {
  const flat = flattenGroups(g);
  return flat.find((route) => route.path === path);
};

export const findActiveRoute = (location: string, routes: NavRoute[]): NavRoute | undefined => {
  return routes.find((route) => match(route.path)(location));
};

export const buildBreadcrumbs = (
  location: string,
  routes: NavRoute[],
  t: Translate,
): NavRoute[] => {
  const split = location.split('/');
  const breadcrumbs: NavRoute[] = [];

  for (let i = 0; i < split.length; i++) {
    const path = i === 0 ? '/' : split.slice(0, i + 1).join('/');
    const found = routes.find((route) => match(route.path)(path));
    if (found) {
      breadcrumbs.push({
        ...found,
        path,
        label: found.label ? t(`lzy.${found.label}`) : split[i],
      });
    }
  }

  return breadcrumbs;
};

export const makeChildren = <T extends string>(input: Record<T, NavRoute<T>>): NavRoute<T>[] => {
  return Object.values(input);
};
